import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import DealContext from '../../../../providers/Deal';
import LeadsContext from '../../../../providers/Leads';
import { useTranslation } from 'react-i18next';
import { LeadStages } from 'models/Lead';
import { SortingButton } from 'router/subrouters/DataRoom/components/SortingButton';
import { SORTING_VALUES } from '../../../LeadsTableContainer/data';
import { Button, Input } from 'ncoded-component-library';
import PencilIcon from 'icons/Pencil.icon';
import { isEnterPressed, isEscapePressed } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './LeadStepperStep.styles.scss';

const MAX_CHAR = 20;

type LeadStepperStepProps = {
  className?: string;
  stage: LeadStages;
  canEditColumnName?: boolean;
};

const LeadStepperStep: React.FC<LeadStepperStepProps> = (props) => {
  const { className, stage, canEditColumnName = false } = props;

  const { popError } = useContext(PopNotificationsContext);
  const { leadStagesCopies, updateLeadStageCopy } = useContext(DealContext);
  const { gridLeads, numberOfOnGoingGridLeads, gridSort, onGridSortStep } =
    useContext(LeadsContext);

  const copy = leadStagesCopies[stage];

  const inputRef = useRef<HTMLLIElement>();

  const [isEdditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(copy);

  const classes = classNames('lead-stepper-step', className);

  const { t } = useTranslation();

  const trimmedInputValue = useMemo(() => inputValue.trim(), [inputValue]);

  const hasError = useMemo(
    () => trimmedInputValue.length > MAX_CHAR,
    [trimmedInputValue.length],
  );

  const getNumOfAll = useCallback(
    (count: number) => `${count}/${numberOfOnGoingGridLeads}`,
    [numberOfOnGoingGridLeads],
  );

  const onSave = useCallback(() => {
    if (!trimmedInputValue) return;

    if (hasError) {
      popError(t('leadStageNameMaxCharError', { maxChar: MAX_CHAR }));
      return;
    }

    updateLeadStageCopy(stage, trimmedInputValue);
    setIsEditing(false);
  }, [trimmedInputValue, stage, hasError, popError, t, updateLeadStageCopy]);

  useEffect(() => {
    const handleClickedOutside = (event: MouseEvent) => {
      if (isEdditing && !inputRef.current?.contains(event.target as Node)) {
        setIsEditing(false);
        setInputValue(copy);
      }
    };

    window.addEventListener('click', handleClickedOutside);

    return () => window.removeEventListener('click', handleClickedOutside);
  }, [copy, isEdditing]);

  return (
    <li className={classes} ref={inputRef}>
      {isEdditing ? (
        <Input
          autoFocus
          value={inputValue}
          hasError={hasError}
          onChange={({ target: { value } }) => setInputValue(value)}
          onKeyDown={(e) => {
            if (isEscapePressed(e)) {
              setIsEditing(false);
              return;
            }

            if (isEnterPressed(e)) {
              onSave();
            }
          }}
          suffixNode={
            <Button
              variant="link"
              disabled={!trimmedInputValue}
              onClick={onSave}
            >
              {t('save')}
            </Button>
          }
        />
      ) : (
        <>
          {copy}

          <span>{getNumOfAll(gridLeads?.[stage]?.length)}</span>

          {canEditColumnName && (
            <Button
              variant="icon"
              icon={PencilIcon}
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            />
          )}

          <SortingButton
            content={t('sort')}
            sortingValue={SORTING_VALUES.lastSeen}
            sort={gridSort?.[stage]}
            updateSort={(sort) => onGridSortStep(stage, sort)}
          />
        </>
      )}
    </li>
  );
};

export default LeadStepperStep;
